import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { firstValueFrom, forkJoin } from 'rxjs';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  heroArrowPath,
  heroCheck,
  heroPencil,
  heroPlus,
  heroTrash,
  heroXMark,
} from '@ng-icons/heroicons/outline';
import { NgpSelectPortal } from 'ng-primitives/select';
import type { AuthPermission } from '../../../core/api/generated/models/AuthPermission';
import { AuthStore } from '../../../core/auth/auth.store';
import {
  ToastrService,
  UiButton,
  UiConfirmDialogService,
  UiDataTable,
  UiDataTableActions,
  UiDataTableCell,
  UiDataTableEmptyCell,
  UiDataTableHead,
  UiDataTableHeaderCell,
  UiDataTableRow,
  UiDataTableShell,
  UiDialog,
  UiDialogDescription,
  UiDialogOverlay,
  UiDialogTitle,
  UiEmptyState,
  UiFilterBar,
  UiFilterField,
  UiFilterLabel,
  UiHint,
  UiInput,
  UiLabel,
  UiPanelCard,
  UiPanelText,
  UiSectionHeader,
  UiSelect,
  UiSelectDropdown,
  UiSelectOption,
} from '../../../shared/ui';
import {
  SettingsCoreApiService,
  type CoreMembership,
  type CoreRole,
  type CoreRoleBinding,
  type CoreTeam,
  type CoreTeamMember,
} from '../settings-core-api.service';

type TeamKind = CoreTeam['kind'];
type TeamMemberRole = CoreTeamMember['role'];

const TEAM_KIND_OPTIONS: readonly { value: TeamKind; label: string }[] = [
  { value: 'general', label: 'Geral' },
  { value: 'sales', label: 'Vendas' },
  { value: 'support', label: 'Suporte' },
  { value: 'success', label: 'Sucesso do cliente' },
  { value: 'operations', label: 'Operações' },
];

const TEAM_MEMBER_ROLE_OPTIONS: readonly { value: TeamMemberRole; label: string }[] = [
  { value: 'member', label: 'Membro' },
  { value: 'lead', label: 'Líder' },
];

@Component({
  selector: 'app-teams-settings-page',
  imports: [
    NgIcon,
    NgpSelectPortal,
    ReactiveFormsModule,
    UiButton,
    UiDataTable,
    UiDataTableActions,
    UiDataTableCell,
    UiDataTableEmptyCell,
    UiDataTableHead,
    UiDataTableHeaderCell,
    UiDataTableRow,
    UiDataTableShell,
    UiDialog,
    UiDialogDescription,
    UiDialogOverlay,
    UiDialogTitle,
    UiEmptyState,
    UiFilterBar,
    UiFilterField,
    UiFilterLabel,
    UiHint,
    UiInput,
    UiLabel,
    UiPanelCard,
    UiPanelText,
    UiSectionHeader,
    UiSelect,
    UiSelectDropdown,
    UiSelectOption,
  ],
  providers: [
    provideIcons({
      heroArrowPath,
      heroCheck,
      heroPencil,
      heroPlus,
      heroTrash,
      heroXMark,
    }),
  ],
  templateUrl: './teams-settings.page.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TeamsSettingsPage {
  private readonly api = inject(SettingsCoreApiService);
  private readonly authStore = inject(AuthStore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(FormBuilder);
  private readonly confirm = inject(UiConfirmDialogService);
  private readonly toastr = inject(ToastrService);

  readonly teamKindOptions = TEAM_KIND_OPTIONS;
  readonly teamMemberRoleOptions = TEAM_MEMBER_ROLE_OPTIONS;
  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly creatingTeam = signal(false);
  readonly teams = signal<CoreTeam[]>([]);
  readonly searchTerm = signal('');
  readonly memberships = signal<CoreMembership[]>([]);
  readonly teamMembers = signal<CoreTeamMember[]>([]);
  readonly roles = signal<CoreRole[]>([]);
  readonly roleBindings = signal<CoreRoleBinding[]>([]);
  readonly teamSavingIds = signal<string[]>([]);
  readonly teamMemberSavingIds = signal<string[]>([]);
  readonly createTeamMemberIds = signal<string[]>([]);
  readonly selectedTeamId = signal<string>('');
  readonly editingTeamId = signal<string>('');
  readonly deletingTeamId = signal<string>('');
  readonly deleteTeamTransferId = signal('');
  readonly deleteTeamMembers = signal<CoreTeamMember[]>([]);
  readonly businessId = computed(() => this.authStore.business()?.id ?? null);
  readonly canCreateTeams = computed(() => this.hasPermission('core:teams:create'));
  readonly canUpdateTeams = computed(() => this.hasPermission('core:teams:update'));
  readonly canArchiveTeams = computed(() => this.hasPermission('core:teams:archive'));
  readonly canManageTeamMembers = computed(() => this.hasPermission('core:teams:manage-members'));
  readonly canAssignRoles = computed(() => this.hasPermission('core:roles:assign'));
  readonly createTeamPermissionHint =
    'Você precisa da permissão core:teams:create para criar equipes.';
  readonly updateTeamPermissionHint =
    'Você precisa da permissão core:teams:update para editar equipes.';
  readonly archiveTeamPermissionHint =
    'Você precisa da permissão core:teams:archive para excluir equipes.';
  readonly manageMembersPermissionHint =
    'Você precisa da permissão core:teams:manage-members para alterar membros.';
  readonly assignRolesPermissionHint =
    'Você precisa da permissão core:roles:assign para alterar funções herdadas.';
  readonly selectedTeam = computed(
    () => this.teams().find((team) => team.id === this.selectedTeamId()) ?? null,
  );
  readonly editingTeam = computed(
    () => this.teams().find((team) => team.id === this.editingTeamId()) ?? null,
  );
  readonly deletingTeam = computed(
    () => this.teams().find((team) => team.id === this.deletingTeamId()) ?? null,
  );
  readonly deleteTransferTeams = computed(() =>
    this.teams().filter((team) => team.id !== this.deletingTeamId() && team.status !== 'archived'),
  );
  readonly canTransferDeletingTeamMembers = computed(
    () => this.deleteTeamMembers().length > 0 && this.deleteTransferTeams().length > 0,
  );
  readonly visibleTeams = computed(() => {
    const term = this.searchTerm().trim().toLocaleLowerCase('pt-BR');
    const activeTeams = this.teams().filter((team) => team.status !== 'archived');
    if (!term) return activeTeams;
    return activeTeams.filter((team) =>
      `${team.name} ${team.slug} ${this.teamKindLabel(team.kind)}`
        .toLocaleLowerCase('pt-BR')
        .includes(term),
    );
  });
  readonly assignableRoles = computed(() =>
    this.roles().filter((role) => role.status === 'active' && role.key !== 'owner'),
  );

  readonly createTeamForm = this.fb.nonNullable.group({
    name: ['', [Validators.required, Validators.maxLength(120)]],
    kind: ['general' as TeamKind, [Validators.required]],
  });

  readonly editTeamForm = this.fb.nonNullable.group({
    name: ['', [Validators.required, Validators.maxLength(120)]],
    kind: ['general' as TeamKind, [Validators.required]],
  });

  constructor() {
    this.load();
  }

  load() {
    const businessId = this.businessId();
    if (!businessId) return;

    this.loading.set(true);
    forkJoin({
      teams: this.api.listTeams(businessId),
      memberships: this.api.listMemberships(businessId),
      roles: this.api.listRoles(businessId),
      roleBindings: this.api.listRoleBindings(businessId),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ teams, memberships, roles, roleBindings }) => {
          this.teams.set(teams.teams.filter((team) => team.status !== 'archived'));
          this.memberships.set(
            memberships.memberships.filter((membership) => membership.status === 'active'),
          );
          this.roles.set(roles.roles.filter((role) => role.status === 'active'));
          this.roleBindings.set(roleBindings.roleBindings);
          this.ensureSelection();
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
          this.toastr.error('Não foi possível carregar equipes.');
        },
      });
  }

  openCreateTeam() {
    if (!this.requirePermission(this.canCreateTeams(), this.createTeamPermissionHint)) return;

    this.createTeamForm.reset({ name: '', kind: 'general' });
    this.createTeamMemberIds.set([]);
    this.creatingTeam.set(true);
  }

  closeCreateTeam() {
    this.creatingTeam.set(false);
    this.createTeamMemberIds.set([]);
  }

  createTeam() {
    const businessId = this.businessId();
    if (!businessId || this.createTeamForm.invalid) return;
    if (!this.requirePermission(this.canCreateTeams(), this.createTeamPermissionHint)) return;
    if (
      this.createTeamMemberIds().length > 0 &&
      !this.requirePermission(this.canManageTeamMembers(), this.manageMembersPermissionHint)
    ) {
      return;
    }

    this.saving.set(true);
    void firstValueFrom(
      this.api.createTeam({
        businessId,
        name: this.createTeamForm.controls.name.value.trim(),
        kind: this.createTeamForm.controls.kind.value,
      }),
    )
      .then(async (team) => {
        const createdMembers = await Promise.all(
          this.createTeamMemberIds().map((membershipId) =>
            firstValueFrom(this.api.addTeamMember(team.id, { membershipId, role: 'member' })),
          ),
        );
        this.teams.update((teams) => [team, ...teams]);
        this.selectTeam(team.id);
        this.teamMembers.set(createdMembers);
        this.closeCreateTeam();
        this.saving.set(false);
        this.toastr.success('Equipe criada.');
      })
      .catch(() => {
        this.saving.set(false);
        this.toastr.error('Não foi possível criar a equipe.');
      });
  }

  updateTeam() {
    const team = this.editingTeam();
    if (!team || this.editTeamForm.invalid) return;
    if (!this.requirePermission(this.canUpdateTeams(), this.updateTeamPermissionHint)) return;

    this.markTeamSaving(team.id, true);
    this.api
      .updateTeam(team.id, {
        name: this.editTeamForm.controls.name.value.trim(),
        kind: this.editTeamForm.controls.kind.value,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => {
          this.replaceTeam(updated);
          this.markTeamSaving(team.id, false);
          this.toastr.success('Equipe atualizada.');
        },
        error: () => {
          this.markTeamSaving(team.id, false);
          this.toastr.error('Não foi possível atualizar a equipe.');
        },
      });
  }

  async archiveTeam(team: CoreTeam) {
    if (!this.requirePermission(this.canArchiveTeams(), this.archiveTeamPermissionHint)) return;

    this.deletingTeamId.set(team.id);
    this.deleteTeamTransferId.set('');
    this.deleteTeamMembers.set(
      this.selectedTeamId() === team.id
        ? this.teamMembers()
        : await this.fetchTeamMembersForTransfer(team.id),
    );
  }

  closeDeleteTeam() {
    this.deletingTeamId.set('');
    this.deleteTeamTransferId.set('');
    this.deleteTeamMembers.set([]);
  }

  selectDeleteTeamTransfer(value: unknown) {
    this.deleteTeamTransferId.set(readSelectString(value));
  }

  deleteTeamTransferLabel() {
    const teamId = this.deleteTeamTransferId();
    return this.deleteTransferTeams().find((team) => team.id === teamId)?.name ?? 'Não transferir';
  }

  async confirmDeleteTeam() {
    const team = this.deletingTeam();
    if (!team) return;
    if (!this.requirePermission(this.canArchiveTeams(), this.archiveTeamPermissionHint)) return;

    this.saving.set(true);
    const transferTeamId = this.deleteTeamTransferId();
    if (transferTeamId && !this.canManageTeamMembers()) {
      this.saving.set(false);
      this.toastr.error(this.manageMembersPermissionHint);
      return;
    }
    const transfer$ = transferTeamId
      ? this.transferDeletingTeamMembers(transferTeamId)
      : Promise.resolve();

    void transfer$
      .then(() => firstValueFrom(this.api.archiveTeam(team.id)))
      .then(() => {
        this.teams.update((teams) => teams.filter((item) => item.id !== team.id));
        this.roleBindings.update((bindings) =>
          bindings.filter((binding) => binding.teamId !== team.id),
        );
        this.editingTeamId.set('');
        this.closeDeleteTeam();
        if (this.selectedTeamId() === team.id) {
          this.selectedTeamId.set('');
          this.teamMembers.set([]);
          this.ensureSelection();
        }
        this.saving.set(false);
        this.toastr.success('Equipe excluída.');
      })
      .catch(() => {
        this.saving.set(false);
        this.toastr.error('Não foi possível excluir a equipe.');
      });
  }

  openEditTeam(team: CoreTeam) {
    if (!this.requirePermission(this.canUpdateTeams(), this.updateTeamPermissionHint)) return;

    this.selectTeam(team.id);
    this.editingTeamId.set(team.id);
  }

  closeEditTeam() {
    this.editingTeamId.set('');
  }

  setSearchTerm(value: string) {
    this.searchTerm.set(value);
  }

  selectTeam(teamId: string) {
    this.selectedTeamId.set(teamId);
    this.patchEditForm();
    this.loadSelectedTeamMembers();
  }

  selectCreateKind(value: unknown) {
    this.createTeamForm.controls.kind.setValue(readTeamKind(value));
  }

  selectEditKind(value: unknown) {
    this.editTeamForm.controls.kind.setValue(readTeamKind(value));
  }

  selectCreateTeamMembers(value: unknown) {
    if (!this.canManageTeamMembers()) return;
    this.createTeamMemberIds.set(readSelectStringArray(value));
  }

  selectedTeamMemberIds() {
    return this.teamMembers().map((member) => member.membershipId);
  }

  selectedTeamRoleIds() {
    const teamId = this.selectedTeamId();
    return this.roleBindings()
      .filter((binding) => binding.teamId === teamId)
      .map((binding) => binding.roleId);
  }

  selectSelectedTeamMembers(value: unknown) {
    const teamId = this.selectedTeamId();
    if (!teamId) return;
    if (!this.requirePermission(this.canManageTeamMembers(), this.manageMembersPermissionHint))
      return;

    const nextMembershipIds = readSelectStringArray(value);
    const currentMembers = this.teamMembers();
    const currentMembershipIds = new Set(currentMembers.map((member) => member.membershipId));
    const nextMembershipIdSet = new Set(nextMembershipIds);
    const additions = nextMembershipIds.filter(
      (membershipId) => !currentMembershipIds.has(membershipId),
    );
    const removals = currentMembers.filter(
      (member) => !nextMembershipIdSet.has(member.membershipId),
    );

    if (!additions.length && !removals.length) return;

    this.markTeamSaving(teamId, true);
    void Promise.all([
      ...additions.map((membershipId) =>
        firstValueFrom(this.api.addTeamMember(teamId, { membershipId, role: 'member' })),
      ),
      ...removals.map((member) =>
        firstValueFrom(this.api.removeTeamMember(teamId, member.membershipId)).then(() => member),
      ),
    ])
      .then((results) => {
        const removedIds = new Set(removals.map((member) => member.membershipId));
        const createdMembers = results.filter(
          (result): result is CoreTeamMember =>
            'membershipId' in result && additions.includes(result.membershipId),
        );
        this.teamMembers.update((members) => [
          ...createdMembers,
          ...members.filter((member) => !removedIds.has(member.membershipId)),
        ]);
        this.markTeamSaving(teamId, false);
        this.toastr.success('Membros atualizados.');
      })
      .catch(() => {
        this.markTeamSaving(teamId, false);
        this.toastr.error('Não foi possível atualizar os membros.');
      });
  }

  selectSelectedTeamRoles(value: unknown) {
    const businessId = this.businessId();
    const teamId = this.selectedTeamId();
    if (!businessId || !teamId) return;
    if (!this.requirePermission(this.canAssignRoles(), this.assignRolesPermissionHint)) return;

    const nextRoleIds = readSelectStringArray(value);
    const currentBindings = this.roleBindings().filter((binding) => binding.teamId === teamId);
    const currentRoleIds = new Set(currentBindings.map((binding) => binding.roleId));
    const nextRoleIdSet = new Set(nextRoleIds);
    const additions = nextRoleIds.filter((roleId) => !currentRoleIds.has(roleId));
    const removals = currentBindings.filter((binding) => !nextRoleIdSet.has(binding.roleId));

    if (!additions.length && !removals.length) return;

    this.markTeamSaving(teamId, true);
    void Promise.all([
      ...additions.map((roleId) =>
        firstValueFrom(this.api.createRoleBinding({ businessId, teamId, roleId })),
      ),
      ...removals.map((binding) =>
        firstValueFrom(this.api.deleteRoleBinding(binding.id)).then(() => binding.id),
      ),
    ])
      .then((results) => {
        const removedIds = new Set(
          results.filter((result): result is string => typeof result === 'string'),
        );
        const createdBindings = results.filter(
          (result): result is CoreRoleBinding => typeof result !== 'string',
        );
        this.roleBindings.update((bindings) => [
          ...createdBindings,
          ...bindings.filter((binding) => !removedIds.has(binding.id)),
        ]);
        this.markTeamSaving(teamId, false);
        this.toastr.success('Funções da equipe atualizadas.');
      })
      .catch(() => {
        this.markTeamSaving(teamId, false);
        this.toastr.error('Não foi possível atualizar as funções da equipe.');
      });
  }

  updateTeamMemberRole(member: CoreTeamMember, role: TeamMemberRole) {
    if (!this.requirePermission(this.canManageTeamMembers(), this.manageMembersPermissionHint))
      return;

    this.markTeamMemberSaving(member, true);
    this.api
      .updateTeamMember(member.teamId, member.membershipId, role)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => {
          this.teamMembers.update((members) =>
            members.map((item) => (item.membershipId === updated.membershipId ? updated : item)),
          );
          this.markTeamMemberSaving(member, false);
          this.toastr.success('Papel na equipe atualizado.');
        },
        error: () => {
          this.markTeamMemberSaving(member, false);
          this.toastr.error('Não foi possível atualizar o membro.');
        },
      });
  }

  async removeTeamMember(member: CoreTeamMember) {
    if (!this.requirePermission(this.canManageTeamMembers(), this.manageMembersPermissionHint))
      return;

    const confirmed = await this.confirm.confirm({
      title: 'Remover membro da equipe?',
      description: 'A pessoa continua na organização, mas deixa de pertencer a esta equipe.',
      confirmLabel: 'Remover',
      tone: 'danger',
    });
    if (!confirmed) return;

    this.markTeamMemberSaving(member, true);
    this.api
      .removeTeamMember(member.teamId, member.membershipId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.teamMembers.update((members) =>
            members.filter((item) => item.membershipId !== member.membershipId),
          );
          this.markTeamMemberSaving(member, false);
          this.toastr.success('Membro removido da equipe.');
        },
        error: () => {
          this.markTeamMemberSaving(member, false);
          this.toastr.error('Não foi possível remover o membro.');
        },
      });
  }

  isTeamSaving(teamId: string) {
    return this.teamSavingIds().includes(teamId);
  }

  isTeamMemberSaving(member: CoreTeamMember) {
    return this.teamMemberSavingIds().includes(teamMemberKey(member));
  }

  membershipLabel(membershipId: string) {
    const membership = this.memberships().find((item) => item.id === membershipId);
    return membership?.user?.displayName || membership?.user?.primaryEmail || membershipId;
  }

  membershipSelectionLabel(membershipIds: string[]) {
    if (!membershipIds.length) return 'Sem membros';
    return membershipIds.map((membershipId) => this.membershipLabel(membershipId)).join(', ');
  }

  roleLabel(roleId: string) {
    return this.roles().find((role) => role.id === roleId)?.name ?? 'Selecionar função';
  }

  roleSelectionLabel(roleIds: string[]) {
    if (!roleIds.length) return 'Sem função herdada';
    return roleIds.map((roleId) => this.roleLabel(roleId)).join(', ');
  }

  teamKindLabel(kind: TeamKind) {
    return TEAM_KIND_OPTIONS.find((option) => option.value === kind)?.label ?? kind;
  }

  memberRoleLabel(role: TeamMemberRole) {
    return TEAM_MEMBER_ROLE_OPTIONS.find((option) => option.value === role)?.label ?? role;
  }

  private ensureSelection() {
    const selected = this.selectedTeamId();
    const teams = this.visibleTeams();
    if (selected && teams.some((team) => team.id === selected)) {
      this.patchEditForm();
      this.loadSelectedTeamMembers();
      return;
    }

    const firstTeam = teams[0];
    if (firstTeam) {
      this.selectTeam(firstTeam.id);
    }
  }

  private fetchTeamMembersForTransfer(teamId: string): Promise<CoreTeamMember[]> {
    return new Promise((resolve) => {
      this.api
        .listTeamMembers(teamId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: ({ members }) => resolve(members),
          error: () => resolve([]),
        });
    });
  }

  private loadSelectedTeamMembers() {
    const teamId = this.selectedTeamId();
    if (!teamId) {
      this.teamMembers.set([]);
      return;
    }

    this.api
      .listTeamMembers(teamId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ members }) => this.teamMembers.set(members),
        error: () => this.toastr.error('Não foi possível carregar membros da equipe.'),
      });
  }

  private patchEditForm() {
    const team = this.selectedTeam();
    if (!team) return;

    this.editTeamForm.patchValue({
      name: team.name,
      kind: team.kind,
    });
  }

  private async transferDeletingTeamMembers(transferTeamId: string) {
    const targetMembers = await firstValueFrom(this.api.listTeamMembers(transferTeamId));
    const existingTargetMembers = new Set(
      targetMembers.members.map((member) => member.membershipId),
    );
    await Promise.all(
      this.deleteTeamMembers()
        .filter((member) => !existingTargetMembers.has(member.membershipId))
        .map((member) =>
          firstValueFrom(
            this.api.addTeamMember(transferTeamId, {
              membershipId: member.membershipId,
              role: member.role,
            }),
          ),
        ),
    );
  }

  private replaceTeam(team: CoreTeam) {
    this.teams.update((teams) => teams.map((item) => (item.id === team.id ? team : item)));
  }

  private hasPermission(permission: AuthPermission) {
    return this.authStore.hasEveryPermission([permission]);
  }

  private requirePermission(allowed: boolean, message: string) {
    if (allowed) return true;
    this.toastr.error(message);
    return false;
  }

  private markTeamSaving(teamId: string, saving: boolean) {
    this.teamSavingIds.update((ids) =>
      saving ? [...new Set([...ids, teamId])] : ids.filter((id) => id !== teamId),
    );
  }

  private markTeamMemberSaving(member: CoreTeamMember, saving: boolean) {
    const key = teamMemberKey(member);
    this.teamMemberSavingIds.update((keys) =>
      saving ? [...new Set([...keys, key])] : keys.filter((item) => item !== key),
    );
  }
}

function readSelectString(value: unknown) {
  if (Array.isArray(value)) {
    return typeof value[0] === 'string' ? value[0] : '';
  }

  return typeof value === 'string' ? value : '';
}

function readSelectStringArray(value: unknown) {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === 'string');
  }

  return typeof value === 'string' && value ? [value] : [];
}

function readTeamKind(value: unknown): TeamKind {
  const selected = readSelectString(value);
  return TEAM_KIND_OPTIONS.some((option) => option.value === selected)
    ? (selected as TeamKind)
    : 'general';
}

function teamMemberKey(member: CoreTeamMember) {
  return `${member.teamId}:${member.membershipId}`;
}
